import React, { useEffect, useRef, useState } from "react";

const depuisChaine = (valeur, longueur) =>
  Array.from({ length: longueur }, (_, i) => (valeur && valeur[i]) || "");

/**
 * Saisie d'un code à `longueur` chiffres dans des cases séparées.
 * `value` est la chaîne assemblée (cases vides omises) ; `onChange` reçoit la
 * nouvelle chaîne. Saisir avance à la case suivante, Retour arrière sur une
 * case vide revient à la précédente, coller répartit les chiffres, saisir
 * plusieurs chiffres dans une case (remplissage automatique) les répartit aussi.
 */
const CasesCode = ({
  value = "",
  onChange,
  longueur = 6,
  erreur = false,
  disabled = false,
  autoFocus = false,
  idPrefixe = "code",
}) => {
  const [cellules, setCellules] = useState(() => depuisChaine(value, longueur));
  const refs = useRef([]);

  // Remise à zéro (ou remplissage) pilotée par le parent.
  useEffect(() => {
    if ((value || "") !== cellules.join("")) {
      setCellules(depuisChaine(value, longueur));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const focus = (i) => {
    const el = refs.current[Math.max(0, Math.min(longueur - 1, i))];
    if (el) {
      el.focus();
      el.select && el.select();
    }
  };

  const appliquer = (suivantes, prochaineCase) => {
    setCellules(suivantes);
    if (onChange) onChange(suivantes.join(""));
    if (prochaineCase !== undefined) focus(prochaineCase);
  };

  const repartir = (depart, chiffres) => {
    const suivantes = [...cellules];
    let i = depart;
    for (const c of chiffres) {
      if (i >= longueur) break;
      suivantes[i] = c;
      i += 1;
    }
    appliquer(suivantes, Math.min(i, longueur - 1));
  };

  const surSaisie = (i, e) => {
    const chiffres = e.target.value.replace(/\D/g, "");
    if (!chiffres) {
      const suivantes = [...cellules];
      suivantes[i] = "";
      appliquer(suivantes);
      return;
    }
    repartir(i, chiffres);
  };

  const surTouche = (i, e) => {
    if (e.key === "Backspace" && !cellules[i] && i > 0) {
      e.preventDefault();
      const suivantes = [...cellules];
      suivantes[i - 1] = "";
      appliquer(suivantes, i - 1);
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focus(i - 1);
    } else if (e.key === "ArrowRight" && i < longueur - 1) {
      e.preventDefault();
      focus(i + 1);
    }
  };

  const surCollage = (e) => {
    const texte = (e.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "");
    if (!texte) return;
    e.preventDefault();
    repartir(0, texte.slice(0, longueur));
  };

  return (
    <div className={"cases" + (erreur ? " erreur" : "")} role="group" aria-label={`Code à ${longueur} chiffres`}>
      {cellules.map((c, i) => (
        <input
          key={i}
          id={`${idPrefixe}-${i + 1}`}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Chiffre ${i + 1}`}
          aria-invalid={erreur ? "true" : undefined}
          value={c}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          onChange={(e) => surSaisie(i, e)}
          onKeyDown={(e) => surTouche(i, e)}
          onPaste={surCollage}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
};

export default CasesCode;
